// Collections / moodboards (Phase 10-4).
//
// Each collection has metadata at `collections/{id}` and items as a
// subcollection at `collections/{id}/items/{designId}`. itemCount and
// coverDesignId are kept in sync by Cloud Function triggers.

import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot,
  orderBy, query, serverTimestamp, setDoc, updateDoc, where,
} from 'firebase/firestore';
import { db, auth } from '../firebase.js';

export const COLLECTION_NAME_MAX = 80;
export const COLLECTION_DESC_MAX = 200;

export const CollectionService = {
  async create({ name, description = '', isPublic = false, isDefault = false }) {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) throw new Error('AUTH_REQUIRED');
    const trimmed = (name || '').trim().slice(0, COLLECTION_NAME_MAX);
    if (!trimmed) throw new Error('NAME_REQUIRED');
    const ref = await addDoc(collection(db, 'collections'), {
      ownerId: user.uid,
      name: trimmed,
      description: (description || '').slice(0, COLLECTION_DESC_MAX),
      isPublic: !!isPublic,
      ...(isDefault && { isDefault: true }),
      itemCount: 0,
      createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  // Returns the user's default 「saved」 collection, creating it if missing.
  // The default collection is the unified bookmark replacement (Phase 10-4
  // option B): a quick-save destination where any design can land without
  // forcing the user to pick a board first. Identified by isDefault=true.
  async ensureDefault(uid, displayName = 'Saved') {
    if (!uid) throw new Error('AUTH_REQUIRED');
    const q = query(
      collection(db, 'collections'),
      where('ownerId', '==', uid),
      where('isDefault', '==', true),
      limit(1),
    );
    const snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
    const id = await this.create({ name: displayName, isDefault: true });
    return { id, name: displayName, isDefault: true, ownerId: uid, itemCount: 0, isPublic: false };
  },

  async getById(collectionId) {
    if (!collectionId) return null;
    const snap = await getDoc(doc(db, 'collections', collectionId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  // Subscribe to a collection's metadata for live count / cover updates.
  subscribe(collectionId, cb) {
    if (!collectionId) { cb(null); return () => {}; }
    return onSnapshot(
      doc(db, 'collections', collectionId),
      (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => cb(null),
    );
  },

  async listMine(uid) {
    if (!uid) return [];
    const q = query(
      collection(db, 'collections'),
      where('ownerId', '==', uid),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  // 프로필에 뿌릴 컬렉션 — 소유자의 것 전부. isPublic 필터를 뺀 이유는
  // getDesignsByUid (profile-service.js) 와 같다: 공개 피드를 접으면서
  // "공개하기" 를 켜는 UI 가 없어졌고, 프로필이 유일한 전시 창구다.
  // 필터를 남겨두면 앞으로 만드는 보드는 영영 프로필에 안 뜬다.
  //
  // 상한 없음 — 예전 limit(24) 는 25번째 보드를 말없이 숨겼다. 보드는 사람당
  // 몇 개 수준이라 (2026-10 최대 2개) 한 번에 다 읽어도 싸다.
  async listByOwner(ownerId) {
    if (!ownerId) return [];
    const q = query(
      collection(db, 'collections'),
      where('ownerId', '==', ownerId),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  // Items in a collection (designIds + addedAt). Returns array. Caller
  // resolves designIds to design docs as needed.
  // max 를 안 주면 전부 — 컬렉션 페이지가 예전 기본값 60 에서 말없이 잘렸다.
  // 표지(CollectionCard)처럼 몇 장만 필요한 쪽은 max 를 넘긴다.
  async listItems(collectionId, { max = null } = {}) {
    if (!collectionId) return [];
    const constraints = [orderBy('addedAt', 'desc')];
    if (max) constraints.push(limit(max));
    const q = query(collection(db, 'collections', collectionId, 'items'), ...constraints);
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  // Add a design to a collection. Idempotent (designId is the doc id).
  async addItem(collectionId, designId) {
    if (!collectionId || !designId) throw new Error('BAD_INPUT');
    return setDoc(doc(db, 'collections', collectionId, 'items', designId), {
      designId,
      addedAt: serverTimestamp(),
    });
  },

  async removeItem(collectionId, designId) {
    if (!collectionId || !designId) return;
    return deleteDoc(doc(db, 'collections', collectionId, 'items', designId));
  },

  async isInCollection(collectionId, designId) {
    if (!collectionId || !designId) return false;
    const snap = await getDoc(doc(db, 'collections', collectionId, 'items', designId));
    return snap.exists();
  },

  // For the AddToCollectionModal: tells which of the user's collections
  // already contain this designId. Single batched query is not possible
  // across subcollections, so we just check each — caller pre-loads the
  // user's collection list which is small.
  async checkMembership(collections, designId) {
    if (!designId || !collections?.length) return new Set();
    const results = await Promise.all(
      collections.map(c => this.isInCollection(c.id, designId).then(ok => ok ? c.id : null)),
    );
    return new Set(results.filter(Boolean));
  },

  async rename(collectionId, name) {
    return updateDoc(doc(db, 'collections', collectionId), {
      name: name.trim().slice(0, COLLECTION_NAME_MAX),
    });
  },

  async setPublic(collectionId, isPublic) {
    return updateDoc(doc(db, 'collections', collectionId), { isPublic: !!isPublic });
  },

  async delete(collectionId) {
    // Items subcollection isn't auto-deleted with the parent — let the
    // counter trigger handle counts on items removal. For 1차 we just
    // delete the parent doc and orphan the items (cheap; reads via the
    // parent rule will fail anyway). A janitor function can clean up.
    return deleteDoc(doc(db, 'collections', collectionId));
  },
};
